import React from 'react';
import Select from 'react-select';

import { Typography } from '@material-tailwind/react';
import { useFormContext } from 'react-hook-form'
import { Controller } from 'react-hook-form';
import { AnimatePresence, motion } from 'framer-motion'

import { findInputError, isFormInvalid } from '../utils'


export interface SelectOption {
    value: string;
    label: string;
};

interface FormTagInputProps {
    id: string;
    name: string;
    label: string;
    isMulti?: boolean;
    labelClass?: string;
    labelColor?: string;
    wrapperClass?: string;
    placeholder: string;
    required?: boolean;
    options: SelectOption[];
}

export const FormTagInput: React.FC<FormTagInputProps> = ({
    id,
    name,
    label,
    labelClass,
    labelColor,
    wrapperClass,
    options,
    placeholder,
    isMulti,
    required
}) => {
    const {
        control,
        formState: { errors },
    } = useFormContext();

    const inputErrors = findInputError(errors, name)
    const isInvalid = isFormInvalid(inputErrors)

    // Custom styles for react-select
    const customStyles = {
        control: (base: any) => ({
            ...base,
            borderRadius: '8px',
            borderShadow: 'rgb(255, 255, 255) 0px 0px 0px 0px',
            marginTop: '3px',
            borderWidth: '1px',
            borderColor: '#e2e8f0',
            color: 'black',
            padding: '0.2rem',
            '&:hover': {
                borderWidth: '1px',
                borderColor: '#cbd5e0',
            },
        }),
        option: (base: any) => ({
            ...base,
            color: 'black',
        }),
        multiValue: (base: any) => ({
            ...base,
            backgroundColor: '#1E293B',
            paddingLeft: '5px',
            color: 'white',
            borderRadius: '4px',
            borderWidth: '2px',
        }),
        multiValueLabel: (base: any) => ({
            ...base,
            color: 'white',
        }),
        multiValueRemove: (base: any) => ({
            ...base,
            color: 'white',
            ':hover': {
                backgroundColor: '#f87171',
                color: 'white',
            },
        }),
        menu: (base: any) => ({
            ...base,
            zIndex: 9999,
            position: 'absolute',
            width: '100%',
            backgroundColor: 'white',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
        }),
        menuPortal: (base: any) => ({
            ...base,
            zIndex: 9999
        }),
    };

    const customStylesError = {
        control: (base: any) => ({
            ...base,
            borderRadius: '5px',
            borderWidth: '2px',
            borderColor: '#fca5a5',
            padding: '0.2rem',
            '&:actve': {
                borderWidth: '2px',
                borderColor: '#fca5a5',
            },
            '&:hover': {
                borderWidth: '2px',
                borderColor: '#fca5a5',
            },
        }),
        option: (base: any) => ({
            ...base,
            color: 'black',
        }),
        multiValue: (base: any) => ({
            ...base,
            backgroundColor: '#1E293B',
            paddingLeft: '5px',
            color: 'white',
            borderRadius: '4px',
            borderWidth: '2px',
        }),
        multiValueLabel: (base: any) => ({
            ...base,
            color: 'white',
        }),
        multiValueRemove: (base: any) => ({
            ...base,
            color: 'white',
            ':hover': {
                backgroundColor: '#f87171',
                color: 'white',
            },
        }),
        menu: (base: any) => ({
            ...base,
            zIndex: 9999,
            position: 'absolute',
            width: '100%',
            backgroundColor: 'white',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
        }),
        menuPortal: (base: any) => ({
            ...base,
            zIndex: 9999
        }),
    };

    return (
        <div className={`${wrapperClass || "mb-6 space-y-1.5"} ${!label ? 'relative' : ''}`}>
            <div className="flex justify-between">
                { label ?
                   <Typography
                        as="label"
                        htmlFor={id}
                        type="small"
                        color="default"
                        className={`${labelClass ? labelColor : 'font-semibold'} ${isInvalid ? 'text-red-500' : (labelColor ? labelColor : 'text-black')}`}
                    >
                        {label}
                    </Typography>
                    : null
                }
                <AnimatePresence mode="wait" initial={false}>
                    {isInvalid && (
                        <motion.p
                            className={`flex items-center gap-1 py-1 mb-1 px-2 font-semibold text-red-500 bg-red-100 rounded-md ${!label ? 'z-10 absolute top-[7px] right-1' : ''}`}
                            {...framer_error}
                        >
                            {inputErrors.error.message}
                        </motion.p>
                    )}
                </AnimatePresence>
            </div>
            <Controller
                name={name}
                control={control}
                rules={{ required: required ? 'This field is required' : false }}
                render={({ field }) => (
                    <Select
                        {...field}
                        id={id}
                        options={options}
                        isMulti={isMulti}
                        className={isInvalid ? 'border-red-300 focus:ring-0 focus:outline-none focus:border-red-500' : ''}
                        placeholder={placeholder}
                        styles={isInvalid ? customStylesError : customStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        onChange={(selectedOption) => {
                            if (isMulti) {
                                // @ts-ignore
                                const values = (selectedOption as Array<{ value: any }>).map(option => option.value);
                                field.onChange(values);
                            } else {
                                field.onChange((selectedOption as { value: any }).value);
                            }
                        }}
                        value={isMulti ? options.filter(option => field.value.includes(option.value)) : options.find(option => option.value === field.value)}
                    />
                )}
            />
        </div>
    );
};

const framer_error = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 10 },
    transition: { duration: 0.2 },
}


export default FormTagInput;
